import { useState } from 'react'
import { trainingProgramActivationApi } from '../../api/trainingProgramActivationApi'
import type { ApiError } from '../../api/client'
import type { TrainingProgramActivation } from '../../types/api'
import { Button } from '../ui'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { captureEvent } from '../../posthog'

interface Props {
  programId: number
  programName: string
  onClose: () => void
  onActivated: (activation: TrainingProgramActivation) => void
}

export default function ActivateProgramSheet({ programId, programName, onClose, onActivated }: Props) {
  const theme = useTheme()
  const { t } = useTranslation()

  const [weeks, setWeeks] = useState(4)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleStart() {
    setLoading(true)
    setError(null)
    try {
      const activation = await trainingProgramActivationApi.activate(programId, weeks)
      captureEvent('program_activated', { program_id: programId, weeks })
      onActivated(activation)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.trainingPrograms.activate.error.activateFailed)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ backgroundColor: theme.bgColor }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 shrink-0"
        style={{ borderBottom: `1px solid ${theme.cardBorder}` }}
      >
        <button
          type="button"
          onClick={onClose}
          className="p-2 -ml-2 rounded-xl"
          style={{ color: theme.text.primary }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
        <h2 className="text-lg font-semibold" style={{ color: theme.text.primary }}>
          {t.trainingPrograms.activate.title}
        </h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <p className="text-sm mb-6" style={{ color: theme.text.muted }}>
          {programName}
        </p>

        {/* Duration picker */}
        <div
          className="rounded-2xl p-4 mb-6"
          style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
        >
          <p className="text-xs font-medium mb-3 uppercase tracking-wide" style={{ color: theme.text.muted }}>
            {t.trainingPrograms.activate.durationLabel}
          </p>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setWeeks(w => Math.max(1, w - 1))}
              disabled={weeks <= 1}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-xl font-bold disabled:opacity-30"
              style={{ backgroundColor: `${theme.buttonColor}18`, color: theme.accent.text }}
            >
              −
            </button>
            <span className="text-2xl font-bold" style={{ color: theme.text.primary }}>
              {t.trainingPrograms.activate.durationWeeks.replace('{{count}}', String(weeks))}
            </span>
            <button
              type="button"
              onClick={() => setWeeks(w => Math.min(52, w + 1))}
              disabled={weeks >= 52}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-xl font-bold disabled:opacity-30"
              style={{ backgroundColor: `${theme.buttonColor}18`, color: theme.accent.text }}
            >
              +
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-xl p-3 mb-4 bg-red-500/10 border border-red-500/25">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 pb-8 pt-3 shrink-0" style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          onClick={handleStart}
          disabled={loading}
        >
          {loading ? t.trainingPrograms.activate.starting : t.trainingPrograms.activate.startButton}
        </Button>
      </div>
    </div>
  )
}
